import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from "recharts";
import { Eye, Printer, Copy, Trash2, Pencil, X, Loader2, Check, Plus, MessageSquare, Users as UsersIcon, FileText, ShieldCheck, Calendar, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISIKO_TONE = {
  hoch:    { chip: "bg-brand-darkred/12 text-brand-darkred border-brand-darkred/30", dot: "bg-brand-darkred", label: "Hoch" },
  mittel:  { chip: "bg-saffron/25 text-clay-dark border-saffron/50", dot: "bg-saffron", label: "Mittel" },
  niedrig: { chip: "bg-sage-dark/12 text-sage-dark border-sage-dark/30", dot: "bg-sage-dark", label: "Niedrig" },
};

const KIND_LABELS = {
  sis: "SIS",
  massnahmen: "Maßnahmenpläne",
  pflegebericht: "Pflegeberichte",
  kassenbrief: "Kassenbriefe",
  hoehergruppe: "Höherstufungen",
  qualitaet: "Qualitätschecks",
};

const KIND_ORDER = ["sis", "massnahmen", "pflegebericht", "kassenbrief", "hoehergruppe", "qualitaet"];

export default function PkiKundenakteDetail({ customerLabel, onBack, onRenamed, onDeleted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewDraft, setPreviewDraft] = useState(null); // full draft loaded
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pflege-ki/kundenakte/kunden/${encodeURIComponent(customerLabel)}`, { withCredentials: true });
      setData(r.data);
    } catch {
      toast.error("Kundenakte konnte nicht geladen werden.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/pflege-ki/kundenakte/kunden/${encodeURIComponent(customerLabel)}`, { withCredentials: true });
        if (!cancel) setData(r.data);
      } catch {
        if (!cancel) toast.error("Kundenakte konnte nicht geladen werden.");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [customerLabel]);

  const chartData = useMemo(() => {
    if (!data?.score_history) return [];
    return data.score_history
      .filter((p) => typeof p.score === "number")
      .map((p) => ({
        date: p.created_at,
        dateLabel: fmtDateShort(p.created_at),
        score: p.score,
        label: p.label,
      }));
  }, [data]);

  const openDraft = async (draftId) => {
    setPreviewLoading(true);
    try {
      const r = await axios.get(`${API}/pflege-ki/kundenakte/drafts/${encodeURIComponent(draftId)}`, { withCredentials: true });
      setPreviewDraft(r.data);
    } catch {
      toast.error("Dokument konnte nicht geladen werden.");
    } finally { setPreviewLoading(false); }
  };

  const deleteKunde = async () => {
    try {
      await axios.delete(`${API}/pflege-ki/kundenakte/kunden/${encodeURIComponent(customerLabel)}`, { withCredentials: true });
      toast.success(`„${customerLabel}" gelöscht.`);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const stamm = data?.stammdaten || {};

  return (
    <div className="space-y-5" data-testid={`pki-kundenakte-detail-${customerLabel}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={onBack}
          data-testid="pki-kundenakte-back"
          className="text-[12.5px] underline text-clay-dark hover:text-brand-darkred font-semibold"
        >
          ← zurück zur Übersicht
        </button>
        <div className="flex items-center gap-2">
          <a
            href={`${API}/pflege-ki/kundenakte/kunden/${encodeURIComponent(customerLabel)}/dossier.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pki-kunde-dossier-pdf"
            className="inline-flex items-center gap-1.5 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 px-4 h-8 text-[12px] font-semibold transition-colors"
            title="Komplette Kundenakte als PDF exportieren (Stammdaten, Verlauf, Dokumente, Audits)"
          >
            <Download className="h-3.5 w-3.5" /> Komplette Akte · PDF
          </a>
          <button
            onClick={() => setEditing(true)}
            data-testid="pki-kunde-edit"
            className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-white hover:border-terra px-3.5 h-8 text-[12px] font-semibold text-sand-700"
          >
            <Pencil className="h-3.5 w-3.5" /> Bearbeiten
          </button>
          <button
            onClick={() => setConfirmDelete(true)}
            data-testid="pki-kunde-delete"
            className="inline-flex items-center gap-1.5 rounded-full border border-brand-darkred/30 bg-white hover:bg-brand-darkred hover:text-white text-brand-darkred px-3.5 h-8 text-[12px] font-semibold"
          >
            <Trash2 className="h-3.5 w-3.5" /> Löschen
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="rounded-3xl border border-cream-300 bg-peach-50 p-6 md:p-7">
        <div className="text-[10.5px] uppercase tracking-[0.3em] font-semibold text-clay">
          Kundenakte
        </div>
        <h2 className="font-display text-2xl md:text-3xl text-sand-800 mt-1.5 leading-tight">
          {customerLabel}
        </h2>
        {(stamm.alter || stamm.pflegegrad || stamm.wohnsituation) && (
          <div className="text-[13px] text-sand-700 mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {stamm.alter && <span><strong>{stamm.alter}</strong> Jahre</span>}
            {stamm.pflegegrad && <span>Pflegegrad <strong>{stamm.pflegegrad}</strong></span>}
            {stamm.wohnsituation && <span className="text-sand-600">· {stamm.wohnsituation}</span>}
          </div>
        )}
        {stamm.notiz && (
          <div className="text-[12.5px] text-sand-600 mt-2 italic leading-relaxed">
            „{stamm.notiz}&ldquo;
          </div>
        )}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <Kpi label="Entwürfe gesamt" value={data.drafts_total ?? 0} />
            <Kpi label="MD-Audits"      value={data.audits_total ?? 0} />
            <Kpi label="Letzter Score"  value={chartData.length ? chartData[chartData.length - 1].score : "–"} />
            <Kpi label="Letztes Risiko"
                 value={chartData.length
                   ? (RISIKO_TONE[chartData[chartData.length - 1].label]?.label || "–")
                   : "–"} />
          </div>
        )}
      </div>

      {loading && <div className="text-sand-600 text-[13px]">lädt …</div>}

      {!loading && data && (
        <>
          {/* Pflegeverlauf · Timeline */}
          <VerlaufTimeline customerLabel={customerLabel} />

          {/* Score-Verlauf */}
          {chartData.length > 0 && (
            <div className="rounded-2xl border border-cream-300 bg-white p-5" data-testid="pki-kundenakte-chart">
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2">
                Score-Verlauf · MD-Vor-Prüfungen
              </div>
              <div className="h-[220px]" data-testid="pki-kundenakte-chart-svg">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <ReferenceArea y1={0}  y2={50}  fill="#8A1A05" fillOpacity={0.06} />
                    <ReferenceArea y1={50} y2={75}  fill="#C18A1A" fillOpacity={0.06} />
                    <ReferenceArea y1={75} y2={100} fill="#3B6E3B" fillOpacity={0.06} />
                    <CartesianGrid strokeDasharray="3 3" stroke="#E9DFC8" />
                    <XAxis dataKey="dateLabel" tick={{ fill: "#7A7269", fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fill: "#7A7269", fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: "#FBF5EA", border: "1px solid #E9DFC8", borderRadius: 12, fontSize: 12 }}
                      labelStyle={{ color: "#23211E", fontWeight: 600 }}
                      formatter={(v) => [v, "Score"]}
                    />
                    <Line type="monotone" dataKey="score" stroke="#8A1A05" strokeWidth={2.5}
                          dot={{ r: 4, fill: "#8A1A05" }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Dokumente pro Bereich */}
          <div className="grid md:grid-cols-2 gap-3" data-testid="pki-kundenakte-drafts">
            {KIND_ORDER.map((kind) => {
              const list = (data.drafts_by_kind?.[kind]) || [];
              return (
                <div key={kind} className="rounded-2xl border border-cream-300 bg-white p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500">
                      {KIND_LABELS[kind] || kind}
                    </div>
                    <span className="text-[11px] font-mono text-sand-700">{list.length}</span>
                  </div>
                  {list.length === 0 ? (
                    <div className="text-sand-600 text-[12.5px]">Noch keine Einträge.</div>
                  ) : (
                    <ul className="space-y-2">
                      {list.map((d) => (
                        <li
                          key={d.id}
                          data-testid={`pki-draft-row-${d.id}`}
                          className="rounded-xl border border-cream-200 bg-cream-50/50 p-2.5 flex items-center gap-2 hover:border-terra/50 transition-colors"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-[12.5px] text-sand-800 truncate font-medium">
                              {KIND_LABELS[kind]?.replace(/e$/, "") || "Entwurf"}
                              {d.status && d.status !== "entwurf" && (
                                <span className="ml-1.5 text-[10px] uppercase tracking-wider text-clay font-bold">
                                  · {d.status}
                                </span>
                              )}
                            </div>
                            <div className="text-[10.5px] text-sand-500">
                              {fmtDateLong(d.updated_at || d.created_at)}
                            </div>
                          </div>
                          <button
                            onClick={() => openDraft(d.id)}
                            data-testid={`pki-draft-view-${d.id}`}
                            title="Ansehen"
                            className="h-8 w-8 rounded-lg hover:bg-white border border-transparent hover:border-cream-300 text-sand-600 hover:text-terra inline-flex items-center justify-center"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          {/* Audit-Tabelle */}
          <div className="rounded-2xl border border-cream-300 bg-white p-5" data-testid="pki-kundenakte-audit-table">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-3">
              Audit-Historie · {data.audits_total} Eintrag/Einträge
            </div>
            {(data.audits || []).length === 0 ? (
              <div className="text-sand-700 text-[13px] py-4">Noch keine Audits.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead>
                    <tr className="text-left text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 border-b border-cream-300">
                      <th className="py-2 pr-3">Datum</th>
                      <th className="py-2 pr-3">Score</th>
                      <th className="py-2 pr-3">Risiko</th>
                      <th className="py-2 pr-3">Hoch</th>
                      <th className="py-2 pr-3">Mittel</th>
                      <th className="py-2 pr-3">Niedrig</th>
                      <th className="py-2 pr-3">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.audits.map((a) => {
                      const s = a.score || {};
                      const t = RISIKO_TONE[s.label] || null;
                      return (
                        <tr key={a.id} className="border-b border-cream-100 last:border-0">
                          <td className="py-2 pr-3 text-sand-800 whitespace-nowrap">{fmtDateLong(a.created_at)}</td>
                          <td className="py-2 pr-3 font-mono text-sand-800">{s.score ?? "–"}</td>
                          <td className="py-2 pr-3">
                            {t ? (
                              <span className={`inline-flex items-center gap-1.5 px-2 h-6 rounded-full border text-[10.5px] uppercase tracking-[0.18em] font-semibold ${t.chip}`}>
                                <span className={`inline-block h-1.5 w-1.5 rounded-full ${t.dot}`} />
                                {t.label}
                              </span>
                            ) : "–"}
                          </td>
                          <td className="py-2 pr-3 font-mono text-brand-darkred">{s.hoch ?? 0}</td>
                          <td className="py-2 pr-3 font-mono text-clay-dark">{s.mittel ?? 0}</td>
                          <td className="py-2 pr-3 font-mono text-sage-dark">{s.niedrig ?? 0}</td>
                          <td className="py-2 pr-3">
                            <a
                              href={`${API}/pflege-ki/md-v2/audit/${a.id}/pdf`}
                              target="_blank" rel="noopener noreferrer"
                              data-testid={`pki-kundenakte-pdf-${a.id}`}
                              className="text-[12px] underline text-clay-dark hover:text-brand-darkred font-semibold"
                            >
                              PDF
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Preview / Print / Copy Modal */}
      {(previewDraft || previewLoading) && (
        <DraftPreviewModal
          draft={previewDraft}
          loading={previewLoading}
          onClose={() => { setPreviewDraft(null); }}
        />
      )}
      {editing && data && (
        <EditKundeModal
          customerLabel={customerLabel}
          initial={stamm}
          onCancel={() => setEditing(false)}
          onSaved={(newLabel) => {
            setEditing(false);
            if (newLabel !== customerLabel) {
              onRenamed?.(newLabel);
            } else {
              reload();
            }
          }}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteModal
          customerLabel={customerLabel}
          draftsTotal={data?.drafts_total || 0}
          auditsTotal={data?.audits_total || 0}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={deleteKunde}
        />
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-2xl border border-cream-300 bg-white p-4">
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-sand-500 font-semibold">{label}</div>
      <div className="font-display text-2xl text-sand-800 leading-tight mt-1">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pflegeverlauf · Timeline
// ---------------------------------------------------------------------------
const VERLAUF_KIND_META = {
  notiz:     { label: "Notiz",         Icon: MessageSquare, color: "text-sand-700",     bg: "bg-cream-100 border-cream-300" },
  gespraech: { label: "Gespräch",      Icon: UsersIcon,     color: "text-brand-darkred", bg: "bg-peach-50 border-terra/30" },
  besuch:    { label: "Besuch",        Icon: Calendar,      color: "text-clay-dark",     bg: "bg-saffron/15 border-saffron/40" },
  event:     { label: "Ereignis",      Icon: ShieldCheck,   color: "text-sage-dark",     bg: "bg-sage-dark/10 border-sage-dark/30" },
  draft:     { label: "Dokument",      Icon: FileText,      color: "text-sand-600",     bg: "bg-white border-cream-300" },
  audit:     { label: "MD-Vor-Prüfung", Icon: ShieldCheck,  color: "text-brand-darkred", bg: "bg-brand-darkred/8 border-brand-darkred/25" },
};

const KIND_OPTIONS_MANUAL = [
  { id: "notiz",     label: "Notiz" },
  { id: "gespraech", label: "Gespräch" },
  { id: "besuch",    label: "Besuch" },
  { id: "event",     label: "Ereignis" },
];

function VerlaufTimeline({ customerLabel }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all"); // all | manual | auto

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/pflege-ki/kundenakte/kunden/${encodeURIComponent(customerLabel)}/verlauf`,
        { withCredentials: true },
      );
      setEntries(r.data?.entries || []);
    } catch {
      toast.error("Verlauf konnte nicht geladen werden.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [customerLabel]);

  const filtered = useMemo(() => {
    if (filter === "manual") return entries.filter((e) => e.source === "manual");
    if (filter === "auto")   return entries.filter((e) => e.source !== "manual");
    return entries;
  }, [entries, filter]);

  const remove = async (entryId) => {
    if (!window.confirm("Diesen Verlaufseintrag wirklich löschen?")) return;
    try {
      await axios.delete(
        `${API}/pflege-ki/kundenakte/verlauf/${encodeURIComponent(entryId)}`,
        { withCredentials: true },
      );
      toast.success("Eintrag gelöscht.");
      await load();
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const manualCount = entries.filter((e) => e.source === "manual").length;

  return (
    <div className="rounded-2xl border border-cream-300 bg-white p-5" data-testid="pki-verlauf">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500">
            Pflegeverlauf
          </div>
          <div className="font-display text-lg text-sand-800 mt-0.5">
            Zeitleiste · {entries.length} Eintrag/Einträge
          </div>
          <p className="text-[11.5px] text-sand-600 mt-1">
            Notizen, Gespräche, Besuche + automatische Ereignisse aus generierten Dokumenten.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-cream-300 bg-cream-50 p-0.5 text-[11.5px]">
            {[
              { id: "all",    label: `Alle · ${entries.length}` },
              { id: "manual", label: `Notizen · ${manualCount}` },
              { id: "auto",   label: `Auto · ${entries.length - manualCount}` },
            ].map((o) => (
              <button
                key={o.id}
                onClick={() => setFilter(o.id)}
                data-testid={`pki-verlauf-filter-${o.id}`}
                className={`px-3 h-7 rounded-full font-semibold transition-colors ${
                  filter === o.id ? "bg-terra text-white" : "text-sand-700 hover:bg-white"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="pki-verlauf-add"
            className="inline-flex items-center gap-1.5 rounded-full bg-terra hover:bg-terra-dark text-white px-4 h-8 text-[12px] font-semibold"
          >
            <Plus className="h-3.5 w-3.5" /> Notiz hinzufügen
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-6 text-sand-500 text-[13px] flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> lädt …
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-8 text-center" data-testid="pki-verlauf-empty">
          <div className="mx-auto h-12 w-12 rounded-full bg-cream-100 flex items-center justify-center mb-3">
            <MessageSquare className="h-5 w-5 text-sand-500" />
          </div>
          <div className="text-[13.5px] text-sand-700 mb-1">Noch keine Einträge im Pflegeverlauf.</div>
          <div className="text-[12px] text-sand-500">
            Notizen erscheinen automatisch, sobald du Dokumente generierst — oder füge eine manuelle Notiz hinzu.
          </div>
        </div>
      ) : (
        <ol className="relative pl-6 space-y-3" data-testid="pki-verlauf-list">
          {/* Vertical line */}
          <span className="absolute left-2 top-1 bottom-1 w-px bg-cream-300" />
          {filtered.map((e) => {
            const meta = VERLAUF_KIND_META[e.kind] || VERLAUF_KIND_META.notiz;
            const Icon = meta.Icon;
            return (
              <li key={e.id} className="relative" data-testid={`pki-verlauf-entry-${e.id}`}>
                <span className={`absolute -left-6 top-2 h-4 w-4 rounded-full flex items-center justify-center border ${meta.bg}`}>
                  <Icon className={`h-2.5 w-2.5 ${meta.color}`} />
                </span>
                <div className={`rounded-xl border ${meta.bg} p-3.5 pl-4 group hover:shadow-hover transition-shadow`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${meta.color}`}>
                          {meta.label}
                        </span>
                        <span className="text-[11px] text-sand-500">
                          {fmtDateLong(e.date)}
                        </span>
                      </div>
                      {e.title && (
                        <div className="text-[13.5px] font-semibold text-sand-800 mt-1">{e.title}</div>
                      )}
                      {e.text && (
                        <div className="text-[13px] text-sand-700 mt-0.5 whitespace-pre-wrap leading-relaxed">
                          {e.text}
                        </div>
                      )}
                    </div>
                    {e.editable && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <button
                          onClick={() => setEditing(e)}
                          data-testid={`pki-verlauf-edit-${e.id}`}
                          title="Bearbeiten"
                          className="h-7 w-7 rounded-lg text-sand-500 hover:text-terra hover:bg-white inline-flex items-center justify-center"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => remove(e.id)}
                          data-testid={`pki-verlauf-delete-${e.id}`}
                          title="Löschen"
                          className="h-7 w-7 rounded-lg text-sand-500 hover:text-brand-darkred hover:bg-white inline-flex items-center justify-center"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {showAdd && (
        <VerlaufFormModal
          customerLabel={customerLabel}
          onCancel={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
      {editing && (
        <VerlaufFormModal
          customerLabel={customerLabel}
          entry={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function VerlaufFormModal({ customerLabel, entry, onCancel, onSaved }) {
  const isEdit = !!entry;
  const [form, setForm] = useState({
    text: entry?.text || "",
    kind: entry?.kind || "notiz",
    event_date: (entry?.date || entry?.event_date || "").slice(0, 10),
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        text: form.text.trim(),
        kind: form.kind,
        event_date: form.event_date || null,
      };
      if (isEdit) {
        await axios.patch(
          `${API}/pflege-ki/kundenakte/verlauf/${encodeURIComponent(entry.id)}`,
          payload,
          { withCredentials: true },
        );
        toast.success("Eintrag aktualisiert.");
      } else {
        await axios.post(
          `${API}/pflege-ki/kundenakte/kunden/${encodeURIComponent(customerLabel)}/verlauf`,
          payload,
          { withCredentials: true },
        );
        toast.success("Notiz hinzugefügt.");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-sand-800/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="pki-verlauf-modal"
        className="bg-white rounded-3xl shadow-hover max-w-lg w-full p-7 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred">
              Pflegeverlauf
            </div>
            <h2 className="font-display text-2xl text-sand-800 mt-1">
              {isEdit ? "Eintrag bearbeiten" : "Notiz hinzufügen"}
            </h2>
          </div>
          <button type="button" onClick={onCancel} className="text-sand-500 hover:text-sand-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-600 mb-1.5">Typ</div>
            <div className="grid grid-cols-4 gap-2">
              {KIND_OPTIONS_MANUAL.map((o) => {
                const meta = VERLAUF_KIND_META[o.id];
                const Icon = meta.Icon;
                const active = form.kind === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setForm({ ...form, kind: o.id })}
                    data-testid={`pki-verlauf-kind-${o.id}`}
                    className={`flex flex-col items-center gap-1 rounded-xl border py-2.5 transition-colors text-[11px] font-semibold ${
                      active
                        ? "border-terra bg-peach-50 text-brand-darkred"
                        : "border-cream-300 bg-cream-50 text-sand-600 hover:border-sand-400"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {o.label}
                  </button>
                );
              })}
            </div>
          </label>

          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-600 mb-1.5">
              Datum (optional)
            </div>
            <input
              type="date" value={form.event_date}
              onChange={(e) => setForm({ ...form, event_date: e.target.value })}
              data-testid="pki-verlauf-date"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra"
            />
            <div className="text-[11px] text-sand-500 mt-1">
              Leer lassen → heute als Datum verwendet.
            </div>
          </label>

          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-600 mb-1.5">
              Text *
            </div>
            <textarea
              rows={4}
              required
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              data-testid="pki-verlauf-text"
              placeholder="z. B. Erstgespräch mit Angehörigen · Sohn Peter kontaktiert"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra resize-none"
            />
          </label>
        </div>

        <div className="mt-6 flex gap-3 justify-end border-t border-cream-300 pt-5">
          <button type="button" onClick={onCancel}
            className="rounded-full px-5 h-10 text-[13px] font-semibold text-sand-700 hover:bg-cream-100">
            Abbrechen
          </button>
          <button type="submit" disabled={busy}
            data-testid="pki-verlauf-submit"
            className="rounded-full bg-terra hover:bg-terra-dark text-white px-6 h-10 text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {isEdit ? "Speichern" : "Notiz hinzufügen"}
          </button>
        </div>
      </form>
    </div>
  );
}


function fmtDateShort(iso) {
  if (!iso) return "–";
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" }); }
  catch { return iso; }
}
function fmtDateLong(iso) {
  if (!iso) return "–";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

// ---------------------------------------------------------------------------
// Preview / Print / Copy Modal
// ---------------------------------------------------------------------------
function DraftPreviewModal({ draft, loading, onClose }) {
  const [copied, setCopied] = useState(false);
  const text = draft?.output_text || "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Text kopiert.");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const printDoc = () => {
    if (!draft) return;
    const kindLabel = KIND_LABELS[draft.kind]?.replace(/e$/, "") || "Dokument";
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) { toast.error("Popup blockiert. Bitte Popup-Blocker deaktivieren."); return; }
    const safe = (s) => (s || "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
    w.document.write(`
      <html><head><title>${safe(kindLabel)} – ${safe(draft.customer_label || "")}</title>
      <style>
        body { font-family: Georgia, 'Times New Roman', serif; padding: 40px; color: #23211E; line-height: 1.65; max-width: 720px; margin: 0 auto; }
        h1 { font-size: 22px; margin: 0 0 6px 0; font-weight: 600; }
        .meta { font-size: 11px; text-transform: uppercase; letter-spacing: 0.2em; color: #7A7269; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 1px solid #E9DFC8; }
        pre { font-family: inherit; white-space: pre-wrap; word-wrap: break-word; font-size: 13.5px; }
        .foot { margin-top: 40px; padding-top: 12px; border-top: 1px solid #E9DFC8; font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: 0.15em; }
      </style></head><body>
        <div class="meta">${safe(kindLabel)} · ${safe(fmtDateLong(draft.updated_at || draft.created_at))}</div>
        <h1>${safe(draft.customer_label || "Kunde")}</h1>
        <pre>${safe(text)}</pre>
        <div class="foot">Dickies-Campus · Pflege-KI · Entwurf, fachlich zu prüfen &amp; freizugeben</div>
      </body></html>
    `);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 250);
  };

  const kindLabel = draft ? (KIND_LABELS[draft.kind]?.replace(/e$/, "") || "Dokument") : "Dokument";

  return (
    <div className="fixed inset-0 z-[100] bg-sand-800/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="pki-draft-preview-modal"
        className="bg-white rounded-3xl shadow-hover max-w-3xl w-full max-h-[92vh] flex flex-col"
      >
        <div className="p-6 pb-4 border-b border-cream-300 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred">
              {kindLabel}
            </div>
            <h2 className="font-display text-xl text-sand-800 mt-1 truncate">
              {draft?.customer_label || "–"}
            </h2>
            {draft && (
              <div className="text-[11.5px] text-sand-500 mt-1">
                {fmtDateLong(draft.updated_at || draft.created_at)}
                {draft.status && ` · Status: ${draft.status}`}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-sand-500 hover:text-sand-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-sand-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> lädt Dokument…
            </div>
          ) : (
            <pre
              data-testid="pki-draft-preview-text"
              className="whitespace-pre-wrap font-serif text-[14px] leading-[1.7] text-sand-800"
            >
              {text || "Kein Text vorhanden."}
            </pre>
          )}
        </div>

        {draft && (
          <div className="p-4 border-t border-cream-300 flex flex-wrap gap-2 justify-end bg-cream-50/50">
            <button
              onClick={copy}
              data-testid="pki-draft-copy"
              className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-white hover:border-terra px-4 h-10 text-[12.5px] font-semibold text-sand-700"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-sage-dark" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Kopiert" : "Kopieren"}
            </button>
            <button
              onClick={printDoc}
              data-testid="pki-draft-print"
              className="inline-flex items-center gap-2 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 px-4 h-10 text-[12.5px] font-semibold"
            >
              <Printer className="h-3.5 w-3.5" /> Drucken
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit-Modal
// ---------------------------------------------------------------------------
function EditKundeModal({ customerLabel, initial, onCancel, onSaved }) {
  const [form, setForm] = useState({
    customer_label: customerLabel,
    alter: initial?.alter ?? "",
    pflegegrad: initial?.pflegegrad ?? "",
    wohnsituation: initial?.wohnsituation ?? "",
    notiz: initial?.notiz ?? "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        customer_label: form.customer_label.trim(),
        alter: form.alter ? Number(form.alter) : null,
        pflegegrad: form.pflegegrad ? Number(form.pflegegrad) : null,
        wohnsituation: form.wohnsituation.trim() || null,
        notiz: form.notiz.trim() || null,
      };
      const r = await axios.patch(
        `${API}/pflege-ki/kundenakte/kunden/${encodeURIComponent(customerLabel)}`,
        payload,
        { withCredentials: true },
      );
      toast.success("Kundenakte aktualisiert.");
      onSaved?.(r.data.customer_label);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-sand-800/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="pki-kunde-edit-modal"
        className="bg-white rounded-3xl shadow-hover max-w-lg w-full p-7 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred">
              Kundenakte
            </div>
            <h2 className="font-display text-2xl text-sand-800 mt-1">Bearbeiten</h2>
          </div>
          <button type="button" onClick={onCancel} className="text-sand-500 hover:text-sand-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-600 mb-1.5">
              Bezeichnung *
            </div>
            <input
              type="text" required minLength={2} value={form.customer_label}
              onChange={(e) => setForm({ ...form, customer_label: e.target.value })}
              data-testid="pki-kunde-edit-label"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-600 mb-1.5">Alter</div>
              <input
                type="number" min="0" max="130" value={form.alter}
                onChange={(e) => setForm({ ...form, alter: e.target.value })}
                data-testid="pki-kunde-edit-alter"
                className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra"
              />
            </label>
            <label className="block">
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-600 mb-1.5">Pflegegrad</div>
              <select
                value={form.pflegegrad}
                onChange={(e) => setForm({ ...form, pflegegrad: e.target.value })}
                data-testid="pki-kunde-edit-pflegegrad"
                className="w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2.5 text-sm"
              >
                <option value="">–</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>PG {n}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-600 mb-1.5">Wohnsituation</div>
            <input
              type="text" value={form.wohnsituation}
              onChange={(e) => setForm({ ...form, wohnsituation: e.target.value })}
              data-testid="pki-kunde-edit-wohn"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra"
            />
          </label>
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-600 mb-1.5">Notiz</div>
            <textarea
              rows={3} value={form.notiz}
              onChange={(e) => setForm({ ...form, notiz: e.target.value })}
              data-testid="pki-kunde-edit-notiz"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra resize-none"
            />
          </label>
        </div>

        <div className="mt-6 flex gap-3 justify-end border-t border-cream-300 pt-5">
          <button type="button" onClick={onCancel}
            className="rounded-full px-5 h-10 text-[13px] font-semibold text-sand-700 hover:bg-cream-100">
            Abbrechen
          </button>
          <button type="submit" disabled={busy}
            data-testid="pki-kunde-edit-submit"
            className="rounded-full bg-terra hover:bg-terra-dark text-white px-6 h-10 text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Speichern
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delete-Confirmation Modal
// ---------------------------------------------------------------------------
function ConfirmDeleteModal({ customerLabel, draftsTotal, auditsTotal, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-[110] bg-sand-800/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="pki-kunde-detail-delete-modal"
        className="bg-white rounded-3xl shadow-hover max-w-md w-full p-7">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred mb-2">
          Löschen bestätigen
        </div>
        <h2 className="font-display text-2xl text-sand-800 mb-3">
          „{customerLabel}&ldquo; löschen?
        </h2>
        <p className="text-[13.5px] text-sand-700 leading-relaxed">
          Es werden <strong>{draftsTotal} Entwürfe</strong> und{" "}
          <strong>{auditsTotal} MD-Audits</strong> unwiderruflich gelöscht.
        </p>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onCancel}
            className="rounded-full px-5 h-10 text-[13px] font-semibold text-sand-700 hover:bg-cream-100">
            Abbrechen
          </button>
          <button
            onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }}
            disabled={busy}
            data-testid="pki-kunde-detail-delete-confirm"
            className="rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white px-6 h-10 text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Endgültig löschen
          </button>
        </div>
      </div>
    </div>
  );
}
